import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { calculateTradeNetMinor } from '@theme/trade-pricing';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('options:price', this.updateLpoPrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('options:price', this.updateLpoPrice);
  }

  updateLpoPrice = (event) => {
    if (!this.hasAttribute('data-lpo-price-sync')) return;

    const eventTarget = event.target instanceof Element ? event.target : null;
    const eventProductForm = eventTarget?.closest('product-form-component');
    if (eventProductForm?.dataset.productId && eventProductForm.dataset.productId !== this.dataset.productId) return;

    const amount = Math.round(Number(event.detail?.price));
    if (!Number.isSafeInteger(amount) || amount < 0) return;
    const tradeContext = this.querySelector('[data-trade-normal-rate]')?.dataset;
    const tradeRate = Number(tradeContext?.tradeNormalRate);
    const normalBaseContext = tradeContext?.tradeNormalBasis === '1';
    const displayAmount = normalBaseContext && [20, 35].includes(tradeRate)
      ? calculateTradeNetMinor(amount, tradeRate) : amount;
    if (displayAmount === null) return;

    const closestSection = this.closest('.shopify-section, dialog');
    const priceRoots = [this];
    const stickyPrice = closestSection?.querySelector('.sticky-add-to-cart__price');
    if (stickyPrice) priceRoots.push(stickyPrice);

    const moneyElements = new Set();
    for (const root of priceRoots) {
      for (const money of root.querySelectorAll(
        '.price__regular [data-rughaus-money], .price-item--sale [data-rughaus-money]'
      )) {
        moneyElements.add(money);
      }
    }

    for (const money of moneyElements) {
      this.updateLpoMoney(money, displayAmount);
    }

    if (this.hasAttribute('data-b2b-price-display')) {
      const qaFixture = window.RUGHAUS_US_DIMENSIONS?.isQaContext(window.RUGHAUS_US_POC_CONTEXT, window.Shopify);
      const retailAmount = normalBaseContext
        ? amount
        : (qaFixture
          ? (window.RUGHAUS_US_POC_STATE?.confirmed?.quote.retailTotalCents ?? window.RUGHAUS_US_POC_CONTEXT.retailBaseCents)
          : Math.round(amount * 1.25));
      const compareMoneyElements = new Set();

      for (const root of priceRoots) {
        for (const money of root.querySelectorAll('.compare-at-price [data-rughaus-money]')) {
          compareMoneyElements.add(money);
        }
      }

      for (const money of compareMoneyElements) {
        this.updateLpoMoney(money, retailAmount);
      }
    }
  };

  updateLpoMoney(money, amount) {
    const nativeCurrency = money.dataset.rughausMoneyCurrency || '';
    const currencyV2Enabled = window.RUGHAUS_PRICING_V2?.enabled === true;
    const tradeContext = window.RUGHAUS_PRICING_V2?.b2b === true;
    const nativeText = currencyV2Enabled
      ? `${nativeCurrency} ${new Intl.NumberFormat('en-US', {
          minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
          maximumFractionDigits: amount % 100 === 0 ? 0 : 2,
        }).format(amount / 100)}`.trim()
      : `${nativeCurrency} ${(amount / 100).toFixed(2)}`.trim();

    money.dataset.rughausMoneyAmount = String(amount);
    money.dataset.rughausMoneyNativeText = nativeText;

    if (currencyV2Enabled || tradeContext) {
      money.textContent = nativeText;
      return;
    }

    const displayMode = money.dataset.rughausMoneyDisplay || 'catalog';
    const isUsd = document.documentElement.dataset.rughausCurrencyDisplay !== 'native';
    const marketRate = Number(window.Shopify?.currency?.rate);

    if (
      displayMode === 'native' ||
      !isUsd ||
      nativeCurrency === 'USD' ||
      !Number.isFinite(marketRate) ||
      marketRate <= 0
    ) {
      money.textContent = nativeText;
      return;
    }

    money.textContent = `USD ${(amount / marketRate / 100).toFixed(2)}`;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    const basePresentmentCents = Number(newProductPrice.dataset.basePresentmentCents);
    if (window.RUGHAUS_PRICING_V2 && Number.isSafeInteger(basePresentmentCents)) {
      window.RUGHAUS_PRICING_V2.basePresentmentCents = basePresentmentCents;
    }

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.hasAttribute('data-lpo-price-sync')) {
      requestAnimationFrame(() => {
        const closestSection = this.closest('.shopify-section, dialog');
        const productForm = [...(closestSection?.querySelectorAll('product-form-component') ?? [])].find(
          (form) => form.dataset.productId === this.dataset.productId && form.querySelector('.cl-po--input')
        );
        const lpoInput = productForm?.querySelector('.cl-po--input');
        lpoInput?.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
